import { computed, ref } from 'vue'
import {
  paymentOrderSocketService,
  type PaymentErrorPayload,
} from '@/services/paymentOrderSocketService'

export type PaymentErrorType = 'channelError' | 'deviceError'

export interface AdminPaymentErrorNotification {
  id: string
  errorType: PaymentErrorType
  channel: string | null
  device: string | null
  count: number
  timestamp: string
  read: boolean
}

const MAX_NOTIFICATIONS = 50

const notifications = ref<AdminPaymentErrorNotification[]>([])
let listenerRegistered = false
let activeConsumers = 0

function normalizeErrorType(type: string): PaymentErrorType {
  return type === 'deviceError' ? 'deviceError' : 'channelError'
}

function buildNotification(payload: PaymentErrorPayload): AdminPaymentErrorNotification {
  const errorType = normalizeErrorType(payload.type)
  const value = String(payload.value ?? '').trim()
  const timestamp = new Date(payload.date || Date.now()).toISOString()

  return {
    id: `${errorType}-${value}-${payload.date}-${payload.count}`,
    errorType,
    channel: errorType === 'channelError' ? value || null : null,
    device: errorType === 'deviceError' ? value || null : null,
    count: Number(payload.count) || 0,
    timestamp,
    read: false,
  }
}

function addNotification(payload: PaymentErrorPayload) {
  const next = buildNotification(payload)
  notifications.value = [next, ...notifications.value].slice(0, MAX_NOTIFICATIONS)
  return next
}

const handlePaymentError = (payload: PaymentErrorPayload) => {
  addNotification(payload)
}

export function useAdminPaymentErrorNotifications() {
  const unreadCount = computed(() => notifications.value.filter((item) => !item.read).length)

  const initializePaymentErrorNotifications = async () => {
    activeConsumers += 1

    if (!listenerRegistered) {
      paymentOrderSocketService.on('payment:error', handlePaymentError)
      listenerRegistered = true
    }

    try {
      await paymentOrderSocketService.connect()
    } catch (error) {
      console.error('Failed to connect payment order socket for error notifications:', error)
    }
  }

  const cleanupPaymentErrorNotifications = () => {
    activeConsumers = Math.max(0, activeConsumers - 1)
    if (activeConsumers > 0) return

    paymentOrderSocketService.off('payment:error', handlePaymentError)
    paymentOrderSocketService.disconnect()
    listenerRegistered = false
  }

  const markAsRead = (id: string) => {
    const target = notifications.value.find((item) => item.id === id)
    if (!target || target.read) return
    target.read = true
  }

  const markAllAsRead = () => {
    if (notifications.value.every((item) => item.read)) return
    notifications.value = notifications.value.map((item) => ({ ...item, read: true }))
  }

  const clearAll = () => {
    notifications.value = []
  }

  return {
    notifications,
    unreadCount,
    initializePaymentErrorNotifications,
    cleanupPaymentErrorNotifications,
    markAsRead,
    markAllAsRead,
    clearAll,
  }
}
